import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PAYMENT_AVAILABILITY_NOTIFY_ENDPOINT } from '../paymentMaintenance';
import './PaymentMaintenanceModal.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PaymentMaintenanceModal = ({ open, onClose, source = 'unknown' }) => {
  const titleId = useId();
  const descriptionId = useId();
  const formStatusId = useId();
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const emailInputRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const [email, setEmail] = useState('');
  const [formState, setFormState] = useState('idle');
  const [formMessage, setFormMessage] = useState('');

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    setEmail('');
    setFormState('idle');
    setFormMessage('');
  }, [open]);

  useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;

    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusFrame = window.requestAnimationFrame(() => {
      emailInputRef.current?.focus();
    });

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key === 'Tab') {
        const focusable = Array.from(
          dialogRef.current?.querySelectorAll(
            'button:not([disabled]), input:not([disabled])'
          ) || []
        );
        if (!focusable.length) return;

        const activeIndex = focusable.indexOf(document.activeElement);
        const nextIndex = event.shiftKey
          ? (activeIndex <= 0 ? focusable.length - 1 : activeIndex - 1)
          : (activeIndex === focusable.length - 1 ? 0 : activeIndex + 1);
        event.preventDefault();
        focusable[nextIndex].focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open]);

  const submitNotificationRequest = async (event) => {
    event.preventDefault();
    if (formState === 'submitting') return;

    const normalizedEmail = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setFormState('error');
      setFormMessage('Enter a valid email address.');
      emailInputRef.current?.focus();
      return;
    }

    setFormState('submitting');
    setFormMessage('');

    try {
      const response = await fetch(PAYMENT_AVAILABILITY_NOTIFY_ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: normalizedEmail, source }),
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok || !payload?.ok) {
        if (payload?.error === 'INVALID_EMAIL') {
          throw new Error('Enter a valid email address.');
        }
        if (response.status === 429) {
          throw new Error('Please wait a few minutes and try again.');
        }
        throw new Error('We could not save your email right now. Please try again.');
      }

      setEmail('');
      setFormState('success');
      setFormMessage("You're on the list. Check your inbox.");
    } catch (error) {
      setFormState('error');
      setFormMessage(error?.message || 'We could not save your email right now. Please try again.');
    }
  };

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="payment-maintenance-overlay"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={dialogRef}
        className="payment-maintenance-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
      >
        <h2 id={titleId}>A quick payment update</h2>

        <div id={descriptionId} className="payment-maintenance-modal__copy">
          Payments are temporarily unavailable. Leave your email and we&apos;ll let you know when they&apos;re back.
        </div>

        <form className="payment-maintenance-modal__notify" onSubmit={submitNotificationRequest} noValidate>
          <label className="payment-maintenance-modal__sr-only" htmlFor={`${titleId}-email`}>
            Email address
          </label>
          <div className="payment-maintenance-modal__notify-row">
            <input
              ref={emailInputRef}
              id={`${titleId}-email`}
              className="payment-maintenance-modal__email"
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (formState === 'error') {
                  setFormState('idle');
                  setFormMessage('');
                }
              }}
              placeholder="you@example.com"
              autoComplete="email"
              inputMode="email"
              maxLength={254}
              aria-invalid={formState === 'error'}
              aria-describedby={formStatusId}
              disabled={formState === 'submitting' || formState === 'success'}
              required
            />
            <button
              type="submit"
              className="payment-maintenance-modal__submit"
              disabled={formState === 'submitting' || formState === 'success'}
            >
              {formState === 'submitting' ? 'Saving...' : formState === 'success' ? 'Done' : 'Notify me'}
            </button>
          </div>
          <p
            id={formStatusId}
            className={`payment-maintenance-modal__notify-status payment-maintenance-modal__notify-status--${formState}`}
            role={formState === 'error' ? 'alert' : undefined}
            aria-live={formState === 'error' ? 'assertive' : 'polite'}
          >
            {formMessage}
          </p>
        </form>

        <button
          ref={closeButtonRef}
          type="button"
          className="payment-maintenance-modal__close"
          onClick={onClose}
        >
          Not now
        </button>
      </section>
    </div>,
    document.body
  );
};

export default PaymentMaintenanceModal;
